export enum DermloopClinicalActionTypeEnum {
  NoAction = 'NoAction',
  Biopsy = 'Biopsy',
  NewImageRegistration = 'NewImageRegistration',
  PlannedBiopsy = 'PlannedBiopsy',
  Dismissal = 'Dismissal',
  Referral = 'Referral',
  FollowUpRequest = 'FollowUpRequest',
  PhysicalReferral = 'PhysicalReferral',
  TeleReferral = 'TeleReferral',
  TopicalTreatment = 'TopicalTreatment',
}

/*
 * Used for rashes in DK (in pilot but maybe afterwards aswell)
 * TODO: Look at all occurences and comments on this after the pilot.
 */
export enum DermloopClinicalActionVariantEnum {
  TopicalTreatment = 'TopicalTreatment',
  ScrapingSwab = 'ScrapingSwab',
  Cryotherapy = 'Cryotherapy',
  OralTreatment = 'OralTreatment',
  Other = 'Other',
  NoTreatment = 'NoTreatment',
  Biopsy = 'Biopsy',
}

export enum ClinicalActionLevelEnum {
  Basic = 'basic',
  Limited = 'limited',
  Full = 'full',
}

export const CLINICAL_ACTIONS: Record<
  ClinicalActionLevelEnum,
  DermloopClinicalActionTypeEnum[]
> = {
  [ClinicalActionLevelEnum.Basic]: [
    DermloopClinicalActionTypeEnum.TeleReferral,
  ],
  [ClinicalActionLevelEnum.Limited]: [
    DermloopClinicalActionTypeEnum.TeleReferral,
    DermloopClinicalActionTypeEnum.FollowUpRequest,
    DermloopClinicalActionTypeEnum.NewImageRegistration,
    DermloopClinicalActionTypeEnum.Dismissal,
  ],
  [ClinicalActionLevelEnum.Full]: [
    DermloopClinicalActionTypeEnum.TeleReferral,
    DermloopClinicalActionTypeEnum.PhysicalReferral,
    DermloopClinicalActionTypeEnum.Biopsy,
    DermloopClinicalActionTypeEnum.FollowUpRequest,
    DermloopClinicalActionTypeEnum.NewImageRegistration,
    DermloopClinicalActionTypeEnum.Dismissal,
    DermloopClinicalActionTypeEnum.PlannedBiopsy,
    DermloopClinicalActionTypeEnum.TopicalTreatment,
  ],
};

export function getClinicalActionLevel(
  actions: string[],
): ClinicalActionLevelEnum {
  if (
    actions.length === 1 &&
    actions[0] === DermloopClinicalActionTypeEnum.TeleReferral
  )
    return ClinicalActionLevelEnum.Basic;
  if (
    actions.length > 1 &&
    actions.includes(DermloopClinicalActionTypeEnum.Biopsy)
  )
    return ClinicalActionLevelEnum.Full;
  return ClinicalActionLevelEnum.Limited;
}

/** The configured action types the server can execute as an independent clinical action. */
export const SUPPORTED_CLINICAL_ACTION_TYPES: readonly DermloopClinicalActionTypeEnum[] =
  [
    DermloopClinicalActionTypeEnum.TeleReferral,
    DermloopClinicalActionTypeEnum.PhysicalReferral,
    DermloopClinicalActionTypeEnum.FollowUpRequest,
    DermloopClinicalActionTypeEnum.Dismissal,
    DermloopClinicalActionTypeEnum.TopicalTreatment,
    DermloopClinicalActionTypeEnum.PlannedBiopsy,
    DermloopClinicalActionTypeEnum.NewImageRegistration,
  ];

/** A tele-referral's state comes from its referral; every other type is `recorded`. */
export type ClinicalActionStatus =
  | 'awaiting-answer'
  | 'answered'
  | 'cancelled'
  | 'closed'
  | 'recorded';

/** Removing an action either soft-deletes it (`remove`) or cancels its unanswered referral (`cancel`). */
export type ClinicalActionRemoval = 'remove' | 'cancel';

export type ClinicalActionLimitReason =
  | 'mixed-action-types'
  | 'tele-referral-awaiting-answer'
  | 'dismissal-twice-in-a-row';

export interface ClinicalActionState {
  id: string;
  actionType: DermloopClinicalActionTypeEnum;
  status: ClinicalActionStatus;
}

/** How an action can be removed, or `null` when it cannot: answered or closed referrals and biopsies stay. */
export function clinicalActionRemoval(
  action: Pick<ClinicalActionState, 'actionType' | 'status'>,
): ClinicalActionRemoval | null {
  if (action.actionType === DermloopClinicalActionTypeEnum.TeleReferral)
    return action.status === 'awaiting-answer' ? 'cancel' : null;
  return SUPPORTED_CLINICAL_ACTION_TYPES.includes(action.actionType)
    ? 'remove'
    : null;
}

/**
 * Why `actionType` cannot be added after a case's `existing` actions (oldest first) and the
 * save's queued removals and earlier adds, or `null` when it can. A save adds actions of one
 * type only; at most one unanswered tele-referral at a time; never two dismissals in a row;
 * every other type is unlimited.
 */
export function clinicalActionLimit(
  actionType: DermloopClinicalActionTypeEnum,
  existing: readonly ClinicalActionState[],
  queued: {
    add?: readonly DermloopClinicalActionTypeEnum[];
    removeIds?: readonly string[];
  } = {},
): ClinicalActionLimitReason | null {
  if (queued.add?.some((type) => type !== actionType))
    return 'mixed-action-types';
  const removed = new Set(queued.removeIds);
  const sequence: Pick<ClinicalActionState, 'actionType' | 'status'>[] = [
    ...existing.flatMap((action) => {
      if (!removed.has(action.id)) return [action];
      const removal = clinicalActionRemoval(action);
      if (removal === 'remove') return [];
      return [
        removal === 'cancel'
          ? { ...action, status: 'cancelled' as const }
          : action,
      ];
    }),
    ...(queued.add ?? []).map((type) => ({
      actionType: type,
      status:
        type === DermloopClinicalActionTypeEnum.TeleReferral
          ? ('awaiting-answer' as const)
          : ('recorded' as const),
    })),
  ];
  if (
    actionType === DermloopClinicalActionTypeEnum.TeleReferral &&
    sequence.some(
      (action) =>
        action.actionType === DermloopClinicalActionTypeEnum.TeleReferral &&
        action.status === 'awaiting-answer',
    )
  )
    return 'tele-referral-awaiting-answer';
  if (
    actionType === DermloopClinicalActionTypeEnum.Dismissal &&
    sequence[sequence.length - 1]?.actionType ===
      DermloopClinicalActionTypeEnum.Dismissal
  )
    return 'dismissal-twice-in-a-row';
  return null;
}

/** Why a configured option cannot be added now: a blocked completion, the type, or a limit. */
export type ClinicalActionAvailabilityReason =
  | 'read-only'
  | 'ehr-managed-submitted'
  | 'assistant-draft-only'
  | 'encounter-required'
  | 'unsupported'
  | 'not-permitted'
  | ClinicalActionLimitReason;

export interface ClinicalActionAvailability {
  optionValue: string;
  available: boolean;
  reason?: ClinicalActionAvailabilityReason;
}
